/**
 * The one shape every statistic in this app is computed from.
 *
 * A completed match. Ported from `LeagueMatchResult`. These accumulate rather than being recomputed
 * on demand because Polymarket's API only exposes roughly the last three weeks of settled events —
 * persisting each result as it is seen is what lets the sample outgrow the API's memory.
 */
export interface MatchResult {
  /** Match slug (`mls-clb-nyc-2026-07-22`) or `espn-{league}-{id}`. Primary key. */
  matchKey: string;
  leagueCode: string;
  /** ISO-8601. */
  playedUtc: string;
  homeTeam: string;
  awayTeam: string;
  homeGoals: number;
  awayGoals: number;
  totalGoals: number;
  bothTeamsScored: boolean;
  /**
   * `'espn'` or `'polymarket'`. **Never mixed when aggregating** — the same fixture from both feeds
   * has different match keys and would be counted twice.
   */
  source: string;
  homeShots?: number | null;
  awayShots?: number | null;
  homeShotsOnTarget?: number | null;
  awayShotsOnTarget?: number | null;
  homePossession?: number | null;
  awayPossession?: number | null;
}

export const SOURCE_ESPN = 'espn';
export const SOURCE_POLYMARKET = 'polymarket';

export const over25 = (r: MatchResult) => r.totalGoals > 2;
export const under35 = (r: MatchResult) => r.totalGoals < 4;

/** Epoch millis for ordering. Rows with no date sort last and are excluded from the backtest. */
export const playedAt = (r: MatchResult) => {
  const t = Date.parse(r.playedUtc);
  return Number.isFinite(t) ? t : Number.NaN;
};

/**
 * C#'s `ToString("0.00")` / `ToString("0")`, which the prompt text is pinned against.
 *
 * `toFixed` is the same round-half-away-from-zero behaviour for the magnitudes here (percentages
 * and goal averages over hundreds of matches), and exact ties are vanishingly rare. Kept as one
 * helper so a formatting difference shows up in one place rather than in nine.
 */
export const fixed = (n: number, places: number) => n.toFixed(places);
